'use client';

import { useTranslations } from 'next-intl';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface RejectablePage {
  /** Stable identity across results — `${resultId}:${pageNumber}`. */
  key: string;
  /** Global 1-based page number. This is the key `page_notes` must use. */
  pageNumber: number;
  /** Display label ("PG-002"), when the container's page list knows it. */
  rawLabel: string | null;
}

interface Props {
  pages: RejectablePage[];
  selected: Set<string>;
  notes: Record<string, string>;
  onToggle: (key: string, next: boolean) => void;
  onNoteChange: (key: string, value: string) => void;
  disabled?: boolean;
}

/**
 * Pick the pages a rejection is actually about, and say what is wrong with
 * each.
 *
 * Shared by the container and per-result verdict dialogs deliberately: they
 * previously each rendered their own copy of this, over page lists derived two
 * different ways, and ended up on the same screen disagreeing about how many
 * pages the container had.
 *
 * Nothing is ticked by default. A reviewer rejecting a container is making a
 * statement about specific pages; demanding a reason for every page (the old
 * behaviour) forced twelve write-ups to report one bad scan, and the pages left
 * alone are what tells the operator where NOT to look. Untouched pages carry no
 * note, which is exactly how `pageQaBucket` reads them as "not yet reviewed".
 */
export function OcrQaPageNotesPicker({
  pages,
  selected,
  notes,
  onToggle,
  onNoteChange,
  disabled
}: Props) {
  const t = useTranslations('ocrQa');
  // One page means there is nothing to choose — just ask for the reason.
  const isSinglePage = pages.length === 1;

  return (
    <div className='space-y-3'>
      <div>
        <p className='text-sm font-medium'>
          {isSinglePage ? t('rejectionReasonLabel') : t('perPageNotesLabel')}
        </p>
        {!isSinglePage && (
          <p className='text-muted-foreground text-xs'>
            {t('pageNoteRequiredHint')}
          </p>
        )}
      </div>
      <div className='space-y-2'>
        {pages.map((page) => {
          const isSelected = isSinglePage || selected.has(page.key);
          const note = notes[page.key] ?? '';
          const missingNote = isSelected && note.trim() === '';
          const label = page.rawLabel
            ? t('pageSectionTitle', { number: page.rawLabel })
            : `#${page.pageNumber}`;

          return (
            <div
              key={page.key}
              className='rounded-lg border p-2.5 transition-colors'
            >
              {isSinglePage ? (
                <Label
                  htmlFor={`ocr-qa-page-note-${page.key}`}
                  className='text-muted-foreground text-xs'
                >
                  {label}
                </Label>
              ) : (
                <div className='flex items-center gap-2'>
                  <Checkbox
                    id={`ocr-qa-page-pick-${page.key}`}
                    checked={isSelected}
                    disabled={disabled}
                    onCheckedChange={(next) =>
                      onToggle(page.key, next === true)
                    }
                  />
                  <Label
                    htmlFor={`ocr-qa-page-pick-${page.key}`}
                    className='cursor-pointer text-xs font-medium'
                  >
                    {label}
                  </Label>
                </div>
              )}

              {isSelected && (
                <Textarea
                  id={`ocr-qa-page-note-${page.key}`}
                  aria-label={label}
                  value={note}
                  onChange={(e) => onNoteChange(page.key, e.target.value)}
                  placeholder={t('pageNotePlaceholder')}
                  rows={2}
                  disabled={disabled}
                  className={missingNote ? 'mt-2 border-red-500/50' : 'mt-2'}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
